import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MainHeading from '@/components/common/MainHeading';
import Layout from '@/components/Layout/Layout';
import { RoutesLink } from '@/utility/RouteLinks';
import {
  IFormData,
  IFormRowData,
  FormType,
} from '@/interfaces/form.model';
import FilterComponent, {
  IFilterArray,
} from '@/components/common/FilterComponent';
import CommonTable, {
  ITableColumn,
  IActionData,
} from '@/components/common/CommonTable';
import CardLayout from '@/components/Layout/CardLayout';
import { FormService } from '@/services/FormService';
import ViewIcon from '@/components/Icons/ViewIcon';
import moment from 'moment';
import { showToastNotification } from '@/utility/helper';
import { setUpdateFilterStates } from '@/redux/features/UserSlice';
import { RootState } from '@/redux/store';

interface IPageStates {
  tableLoader: boolean;
  searchQuery: string;
  formType: string;
}

const MyForm = (): React.ReactNode => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState<IFormRowData[]>([]);

  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );

  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  const [pageStates, setPageStates] = useState<IPageStates>({
    tableLoader: false,
    searchQuery: '',
    formType: FormType.MyForms,
  });

  const fetchForms = useCallback(async () => {
    setPageStates((prev: IPageStates) => ({ ...prev, tableLoader: true }));
    try {
      const filter = {
        pageNumber: filterDetails.currentPage,
        pageSize: filterDetails.pageSize,
        search: pageStates.searchQuery,
        orderBy: filterDetails.orderBy,
        formType: pageStates.formType,
      };

      const response = await FormService.getForms(filter);
      console.log(response, 'response');

      if (response?.status) {
        const forms = response.data?.officeForm || [];
        const newData = forms?.map((officeForm: IFormData, idx: number) => ({
          ...officeForm,
          id: officeForm?.id,
          sno:
            (filterDetails.currentPage - 1) * (filterDetails.pageSize || 10) +
            idx +
            1,
          // Ensure basic fields are present for processedData mapping later
          name: officeForm?.name ?? '--',
          description: officeForm?.description ?? '--',
          totalColumns: officeForm?.columns?.length ?? 0,
          status: officeForm?.status ?? '--',
          createdAt: moment(officeForm?.createdAt).format('DD-MM-YYYY') ?? '--',
        }));
        setTableData(newData as IFormRowData[]);
        dispatch(
          setUpdateFilterStates({
            count: response.data?.pageInfo?.total ?? 0,
            totalPages: response.data?.pageInfo?.totalPages ?? 1,
          }),
        );
      } else {
        showToastNotification(
          'danger',
          response?.message || 'Failed to fetch forms',
        );
      }
    } catch (error: unknown) {
      showToastNotification(
        'danger',
        (error as Error)?.message || 'Error fetching forms',
      );
    } finally {
      setPageStates((prev: IPageStates) => ({ ...prev, tableLoader: false }));
    }
  }, [
    filterDetails.currentPage,
    filterDetails.pageSize,
    filterDetails.orderBy,
    pageStates.searchQuery,
    pageStates.formType,
    dispatch,
  ]);
  const fetchSubmissionFormsData = useCallback(async () => {
    setPageStates((prev: IPageStates) => ({ ...prev, tableLoader: true }));
    try {
      const filter = {
        pageNumber: filterDetails.currentPage,
        pageSize: filterDetails.pageSize,
        search: pageStates.searchQuery,
        orderBy: filterDetails.orderBy,
      };

      const response = await FormService.getSubmissionForms(undefined, filter);
      console.log(response, 'response');

      if (response?.status) {
        const forms = response.data?.officeForm || [];
        const newData = forms?.map((officeForm: IFormData, idx: number) => ({
          ...officeForm,
          sno:
            (filterDetails.currentPage - 1) * (filterDetails.pageSize || 10) +
            idx +
            1,

          // Ensure basic fields are present for processedData mapping later
          name: officeForm?.form?.name ?? '--',
          submittedBy: officeForm?.submittedBy,
          submittedByName: officeForm?.submittedBy?.name ?? '--',
          description: officeForm?.description ?? '--',
          totalColumns: officeForm?.data?.length ?? 0,
          status: officeForm?.status ?? '--',
          createdAt: moment(officeForm?.createdAt).format('DD-MM-YYYY') ?? '--',
          submittedAt:
            moment(officeForm?.submittedAt).format('DD-MM-YYYY') ?? '--',
        }));
        setTableData(newData as IFormRowData[]);
        dispatch(
          setUpdateFilterStates({
            count: response.data?.pageInfo?.total ?? 0,
            totalPages: response.data?.pageInfo?.totalPages ?? 1,
          }),
        );
      } else {
        showToastNotification(
          'danger',
          response?.message || 'Failed to fetch forms',
        );
      }
    } catch (error: unknown) {
      showToastNotification(
        'danger',
        (error as Error)?.message || 'Error fetching forms',
      );
    } finally {
      setPageStates((prev: IPageStates) => ({ ...prev, tableLoader: false }));
    }
  }, [
    filterDetails.currentPage,
    filterDetails.pageSize,
    filterDetails.orderBy,
    pageStates.searchQuery,
    dispatch,
  ]);

  useEffect(() => {
    if (pageStates.formType === FormType.MyForms) {
      fetchForms();
    } else {
      fetchSubmissionFormsData();
    }
  }, [pageStates.formType, fetchForms, fetchSubmissionFormsData]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    timeoutId.current = setTimeout(() => {
      dispatch(setUpdateFilterStates({ currentPage: 1 }));
      setPageStates((prevState: IPageStates) => ({
        ...prevState,
        searchQuery: value,
      }));
    }, 500);
  };

  const pageChangeHandler = (PageNo: number) => {
    if (PageNo > 0 && PageNo <= filterDetails?.totalPages) {
      dispatch(setUpdateFilterStates({ currentPage: PageNo }));
    }
  };

  const handleChangePageSize = (value: number) => {
    dispatch(setUpdateFilterStates({ pageSize: value, currentPage: 1 }));
  };

  const tableHeading: ITableColumn<IFormRowData>[] = [
    { label: 'S.No.', value: 'sno' },
    { label: 'Form Name', value: 'name' },
    ...(pageStates.formType === FormType.TeamForms
      ? [
        {
          label: 'Submitted By',
          value: 'submittedByName' as keyof IFormRowData,
        },
      ]
      : []),
    { label: 'Total Columns', value: 'totalColumns' },
    { label: 'Status', value: 'formStatus' },
    // { label: 'Created At', value: 'formCreatedAt' },
    ...(pageStates.formType === FormType.TeamForms
      ? [{ label: 'Submitted At', value: 'submittedAt' as keyof IFormRowData }]
      : [{ label: 'Created At', value: 'createdAt' as keyof IFormRowData }]),
  ];

  const url = (data: IFormRowData) => {
    if (pageStates?.formType === FormType.MyForms) {
      return `${RoutesLink.formView}/${data?.id}`;
    } else {
      return `${RoutesLink.formView}/${data?.formId}?userId=${data?.submittedBy?.id}`;
    }
  };

  const actionData: IActionData<IFormRowData>[] = [
    {
      label: 'View',
      className: 'btn btn-outline-secondary p-1 border-0',
      icon: <ViewIcon />,
      title: 'View',

      clickHandler: (data) =>
        navigate(url(data), {
          state: {
            id: pageStates.formType !== FormType.MyForms ? data?.id : '',
            isSubmitted: pageStates.formType === FormType.MyForms ? true : false,
            isShow: pageStates.formType === FormType.TeamForms ? true : false,
            formType: pageStates.formType,
          },
        }),
    },
  ];
  // console.log(actionData, "actionData");

  const filterArray: IFilterArray<boolean> = {
    isShow: false,
  };

  // Convert status to badge styling and format date
  const processedData: IFormRowData[] = tableData?.map((item) => {
    // Extract status string safely if it's an object
    const statusStr = item?.status;
    // console.log("statusStr", statusStr);

    let statusClass = 'bg-secondary';
    const lowerStatus = String(statusStr || '').toLowerCase();

    if (lowerStatus === 'published' || lowerStatus === 'active')
      statusClass = 'bg-success';
    if (lowerStatus === 'draft' || lowerStatus === 'pending')
      statusClass = 'bg-warning ';
    if (lowerStatus === 'submitted' || lowerStatus === 'inactive')
      statusClass = 'bg-primary';

    return {
      ...item,
      formStatus: (
        <span
          className={`badge ${statusClass} fw-normal rounded-pill py-1 fs-6 text-white`}
        >
          {statusStr ? String(statusStr)?.toUpperCase() : 'N/A'}
        </span>
      ),
      formCreatedAt: item?.createdAt
        ? moment(item?.createdAt).format('DD MMM YYYY, hh:mm A')
        : '--',
    };
  });

  const handleFormTypeChange = (type: string) => {
    setPageStates((prevState: IPageStates) => ({
      ...prevState,
      formType: type,
    }));
  };

  return (
    <Layout>
      <MainHeading title="My Forms" showbBackButton={true} />
      <div className="d-flex gap-3 mb-4 ">
        <div>
          <button
            onClick={() => handleFormTypeChange(FormType.MyForms)}
            className={`btn ${pageStates.formType === FormType.MyForms ? 'btn-primary' : 'btn-outline-primary'} w-100 ps-4`}
          >
            My Form
          </button>
        </div>
        <div>
          <button
            onClick={() => handleFormTypeChange(FormType.TeamForms)}
            className={`btn ${pageStates.formType === FormType.TeamForms ? 'btn-primary' : 'btn-outline-primary'} w-100 px-4`}
          >
            Team Form
          </button>
        </div>
      </div>
      <CardLayout>
        <FilterComponent
          searchBar={true}
          searchPlaceHolder="Search Forms"
          handleSearch={handleSearchChange}
          filterArray={filterArray}
        />
        <CommonTable
          tableData={processedData}
          tableHeading={tableHeading}
          count={filterDetails.count}
          pageSize={filterDetails.pageSize || 10}
          currentPage={filterDetails.currentPage}
          totalPages={filterDetails.totalPages}
          pageChangeHandler={pageChangeHandler}
          handleChangePageSize={handleChangePageSize}
          isLoading={pageStates.tableLoader}
          ActionData={actionData}
        />
      </CardLayout>
    </Layout>
  );
};

export default MyForm;
